import { useAuth0 } from '@auth0/auth0-react';
import { OxCategoriesKeys } from '@oxappsec/ox-consolidated-categories';
import { getEnvironmentType, isProd, isRemoteSite } from 'env-utils';
import { logger } from 'logging-utils';
import { FC, ReactElement, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { fetchAssignedAppOwners } from '../../application-owners/actions/applications-owners-actions';
import { isOxUserEmail } from '../../common/common-utils';
import AppSpinner from '../../common/components/AppSpinner';
import { setLoggedInUserConnection } from '../../connections/utils/selected-connection-store-utils';
import { loadConnectorsByFamily } from '../../connectors/actions/connector-actions';
import { loadDashboardInfo } from '../../executive-summary/actions/executive-summary-actions';

import { AppPages } from '../../common/routing/app-navigator';
import { setLoggedInUserId } from '../../members/api/members-api';
import { setUserRoles } from '../../members/api/user-roles-api';
import { CUSTOM_ROLES } from '../../members/types/user-role-types';
import { fetchScanSettingsAndStatus } from '../../new-scan/actions/scan-actions';
import { isOnboardingRequired } from '../../onboarding/actions/onboarding-actions';
import {
  getAvailableOrganizations,
  setChosenOrganization,
  useOrganizations,
} from '../../organizations/api/organizations-api';
import { getLastLoggedInOrg } from '../../organizations/utils/local-storage-utils';
import { fetchActiveProfileOverviewAction } from '../../policies/api/policies-api';
import TelemetryManager from '../../telemetry';
import { initAppcues } from '../../telemetry/appcues';
import {
  logUserIntoOrganization,
  setGeneralError,
  setPageError,
  useDebugToggle,
} from '../actions/app-general-actions';
import {
  checkIfEmptyOrg,
  dispatchAppReady,
  initApp,
} from '../actions/app-init-actions';
import { registerUserConsent } from '../actions/app-registration-actions';
import AppStore from '../stores/app-store';
import { loadExclusions } from '../../exclusions/actions/exclusions-actions';
import { auditUserLogin } from '../actions/app-login-actions';
import { LogName } from '@oxappsec/ox-unified-audit-logger';

const AppLoader: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { initialized, loadingNewToken, isFirstLogin } = useSnapshot(AppStore);
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { organizations } = useOrganizations();
  useDebugToggle();

  useEffect(() => {
    if (user && !user.email_verified) {
      navigate(AppPages.EmailVerification);
    }
  }, [navigate, user]);

  useEffect(() => {
    if (user) {
      AppStore.user = user;
      // setup telemetry with the current user
      TelemetryManager.setTelemetryUser({
        id: user.sub,
        email: user.email,
        orgId: user.org_id,
      });
      TelemetryManager.setTelemetryAction('Login', user);
      // In production start recording sessions
      if (isProd() && !isOxUserEmail(user.email)) {
        TelemetryManager.startRumRecordingSession();
      }

      // setup user roles
      setUserRoles(user[CUSTOM_ROLES]);

      // members page - setting the current user id
      setLoggedInUserId(user.sub);

      // login settings page - setting the current login connection
      setLoggedInUserConnection(user.sub);

      // App-cues (formerly known as WalkMe)
      if (!isRemoteSite()) {
        initAppcues(user);
      }
    }
  }, [user]);

  useEffect(() => {
    if (isFirstLogin) {
      return;
    }

    // should be preformed only if the user has organizations (not to be confused with OrgId)
    logUserIntoOrganization(
      isAuthenticated,
      organizations,
      getAccessTokenSilently,
      getAvailableOrganizations,
    )
      .then(onboardingStarted => {
        // Telemetry logs need for the onboarding
        TelemetryManager.addTelemetryLogsContext('env', getEnvironmentType());
        TelemetryManager.addTelemetryLogsContext('user', user);

        // nvaigate user to the onboarding flow
        if (user?.email_verified && onboardingStarted) {
          AppStore.isReady = true;
          navigate(AppPages.Onboarding);
          return;
        }

        const lastLoggedInOrg = getLastLoggedInOrg();
        const noLastLoginOrg = !lastLoggedInOrg && organizations.length !== 0;

        // Providing organization to telemetry context
        TelemetryManager.setTelemetryContext({
          ...(noLastLoginOrg ? organizations[0] : lastLoggedInOrg),
        });
        TelemetryManager.addTelemetryLogsContext(
          'organization',
          noLastLoginOrg ? organizations[0] : lastLoggedInOrg,
        );

        // The actual setting of organization for a user
        setChosenOrganization(
          noLastLoginOrg ? organizations[0] : lastLoggedInOrg,
        );
      })
      .catch(e => {
        logger.error(e);
        setGeneralError(true);
        setPageError(true);
      });
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    organizations,
    isFirstLogin,
    navigate,
    user,
  ]);

  useEffect(() => {
    if (!initialized) {
      initApp();
    }
  }, [initialized]);

  const isLoadDashboardInfoCalled = useRef(false);

  useEffect(() => {
    const isMatchingPage =
      location.pathname === AppPages.Onboarding ||
      location.pathname === AppPages.IDPConfigure;
    if (
      isMatchingPage ||
      isFirstLogin ||
      (location.pathname === AppPages.Connectors && !!location.search)
    ) {
      return;
    }
    if (
      user &&
      user.org_id &&
      user.email_verified &&
      !loadingNewToken &&
      !isLoadDashboardInfoCalled.current
    ) {
      isLoadDashboardInfoCalled.current = true;

      // Get the policy count (active/disabled) to display in top bar.
      fetchActiveProfileOverviewAction();
      loadExclusions();
      loadDashboardInfo().then(lastReport => {
        loadConnectorsByFamily().then(connectorsByFamily => {
          // move isOnBoardingRequired to backend.
          const sourceControlConnectors = connectorsByFamily.find(
            familyObject =>
              familyObject.family === OxCategoriesKeys.SourceControl,
          );
          if (sourceControlConnectors) {
            const navigateToOnboarding =
              isOnboardingRequired(sourceControlConnectors.connectors) &&
              !lastReport;
            if (navigateToOnboarding) {
              navigate(AppPages.Onboarding);
              return;
            }
          }
          // In home root - default page dashboard
          if (location.pathname === AppPages.Home) {
            navigate(AppPages.Dashboard);
          }
        });
      });
    }
  }, [
    loadingNewToken,
    location.pathname,
    navigate,
    user,
    isFirstLogin,
    location.search,
  ]);

  useEffect(() => {
    if (user && user.org_id && !loadingNewToken) {
      checkIfEmptyOrg();
      fetchScanSettingsAndStatus();
      registerUserConsent(user);
      auditUserLogin(user, LogName.Login);
      fetchAssignedAppOwners();
      dispatchAppReady(user);
    }
  }, [loadingNewToken, user]);

  if (initialized) return children as ReactElement;

  return <AppSpinner />;
};

export default AppLoader;
